import PT from 'prop-types';
import React, { useState } from 'react';

import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import TextInput from '~/ui/components/TextInput/TextInput';
import { useEscape, useHover } from '~/ui/hooks/interaction';
import styles from '~/ui/styles';

function EditableText({
  disabled = false,
  id,
  isOutsideHover = false,
  placeholder: initialPlaceholder,
  width,
  value,
  onChange,
  error,
}) {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const [ref, isHovered] = useHover();

  const placeholder =
    initialPlaceholder ??
    intl.formatMessage({
      id: 'EditableText.untitled',
      defaultMessage: 'Untitled',
      description: 'Default placeholder text for editable text input',
    });

  function cancel() {
    setDraftValue(value);
    setIsEditing(false);
  }

  function submit(e) {
    e.preventDefault();

    if (draftValue) {
      onChange(draftValue);
    }
    setIsEditing(false);
  }

  useEscape(cancel);

  function getWidth(theme) {
    if (!width || !theme.sizes[`input-width-${width}`]) {
      return '100%';
    }
    return theme.sizes[`input-width-${width}`];
  }

  if (isEditing) {
    return (
      <Box as="form" sx={{ width: getWidth }} onBlur={submit} onSubmit={submit}>
        <TextInput
          autoFocus
          placeholder={placeholder}
          value={draftValue}
          width="fullWidth"
          onChange={setDraftValue}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 'auto' }}>
      <FlexLayout
        alignItems="center"
        bg={isHovered && !isOutsideHover ? 'black-alpha-04' : null}
        disabled={disabled}
        justifyContent="space-between"
        px={4}
        ref={ref}
        space={3}
        sx={{
          borderRadius: 'm',
          height: 'input-height',
          width: getWidth,
          borderColor: error ? 'red-400' : null,
          borderWidth: error ? 1 : null,
          borderStyle: error ? 'solid' : null,
        }}
        onClick={() => setIsEditing(true)}
      >
        <Text
          color={error ? 'red-400' : value ? 'gray-900' : 'gray-600'}
          variant="m-dense-bold"
          sx={{ ...styles.typography.truncate }}
        >
          {value || placeholder}
        </Text>
        {(isHovered || isOutsideHover) && !disabled ? (
          <IconButton icon="edit" id={id} size="s" />
        ) : null}
      </FlexLayout>
      {error && (
        <Text color="red-400" variant="tiny">
          {error}
        </Text>
      )}
    </Box>
  );
}

EditableText.propTypes = {
  disabled: PT.bool,
  /** ID */
  id: PT.string,
  /** Boolean indicating if container was hovered */
  isOutsideHover: PT.bool,
  /** Placeholder */
  placeholder: PT.string,
  /** Current text value */
  value: PT.string,
  /** Callback that returns new text value */
  onChange: PT.func.isRequired,
};

export default EditableText;
